import createDOMPurify, { type DOMPurify } from "dompurify";
import React from "react";

// -------------------------------------------------------------
// DOM helpers / shared utilities
// -------------------------------------------------------------

const DISALLOWED_HTML_TAGS = new Set(["script", "style"]);
const HEADING_REGEX = /^(#{1,6})\s+(.*)$/;
const INLINE_BOLD_REGEX = /\*\*(.+?)\*\*/g;
const INLINE_ITALIC_REGEX = /\*(.+?)\*/g;
const NODE_TYPE = {
  ELEMENT: 1,
  TEXT: 3,
} as const;

const canUseDomParser = () =>
  typeof window !== "undefined" && typeof window.DOMParser !== "undefined";
const isHtmlLike = (value: string) => value.includes("<");
const getDomPurify = (() => {
  let instance: DOMPurify | null = null;
  return (): DOMPurify | null => {
    if (typeof window === "undefined") {
      return null;
    }
    if (!instance) {
      instance = createDOMPurify(window);
    }
    return instance;
  };
})();

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const applyInlineMarkdown = (value: string): string => {
  const escaped = escapeHtml(value);
  return escaped
    .replace(INLINE_BOLD_REGEX, "<strong>$1</strong>")
    .replace(INLINE_ITALIC_REGEX, "<em>$1</em>");
};

const toCamelCase = (value: string) =>
  value.replace(/-([a-z])/g, (_match, char: string) => char.toUpperCase());

/**
 * Comprehensive whitelist of safe CSS properties for inline styles.
 *
 * This prevents CSS injection attacks through dangerous properties like:
 * - expression() (IE code execution)
 * - behavior (IE HTC files)
 * - binding (Mozilla XBL)
 * - -moz-binding
 * - background-image: url() (data exfiltration)
 *
 * Includes extensive support for:
 * - Text styling (fonts, colors, alignment, decoration)
 * - Layout (box model, positioning, sizing)
 * - Borders and outlines (all variants)
 * - Flexbox (complete specification)
 * - Grid (complete specification)
 * - Tables and lists
 * - Safe transforms and transitions
 * - Modern CSS properties (aspect-ratio, object-fit, etc.)
 *
 * This ensures good compatibility with rich text from Google Docs, Microsoft Word,
 * and other editors while maintaining security.
 *
 * Only visual styling properties that don't execute code or load external resources are allowed.
 */
const SAFE_CSS_PROPERTIES = new Set([
  // Text styling
  "color",
  "fontFamily",
  "fontSize",
  "fontStyle",
  "fontWeight",
  "fontVariant",
  "fontStretch",
  "lineHeight",
  "letterSpacing",
  "wordSpacing",
  "textAlign",
  "textDecoration",
  "textDecorationColor",
  "textDecorationLine",
  "textDecorationStyle",
  "textIndent",
  "textTransform",
  "whiteSpace",
  "wordBreak",
  "wordWrap",
  "textOverflow",
  "direction",
  "unicodeBidi",

  // Layout
  "display",
  "position",
  "top",
  "right",
  "bottom",
  "left",
  "width",
  "height",
  "minWidth",
  "minHeight",
  "maxWidth",
  "maxHeight",
  "margin",
  "marginTop",
  "marginRight",
  "marginBottom",
  "marginLeft",
  "padding",
  "paddingTop",
  "paddingRight",
  "paddingBottom",
  "paddingLeft",
  "overflow",
  "overflowX",
  "overflowY",

  // Border
  "border",
  "borderTop",
  "borderRight",
  "borderBottom",
  "borderLeft",
  "borderWidth",
  "borderTopWidth",
  "borderRightWidth",
  "borderBottomWidth",
  "borderLeftWidth",
  "borderStyle",
  "borderTopStyle",
  "borderRightStyle",
  "borderBottomStyle",
  "borderLeftStyle",
  "borderColor",
  "borderTopColor",
  "borderRightColor",
  "borderBottomColor",
  "borderLeftColor",
  "borderRadius",
  "borderTopLeftRadius",
  "borderTopRightRadius",
  "borderBottomLeftRadius",
  "borderBottomRightRadius",
  "borderCollapse",
  "borderSpacing",
  "outline",
  "outlineColor",
  "outlineStyle",
  "outlineWidth",
  "outlineOffset",

  // Background (excluding background-image for security)
  "backgroundColor",

  // Flexbox
  "flex",
  "flexDirection",
  "flexWrap",
  "flexGrow",
  "flexShrink",
  "flexBasis",
  "flexFlow",
  "justifyContent",
  "justifyItems",
  "justifySelf",
  "alignItems",
  "alignContent",
  "alignSelf",
  "gap",
  "rowGap",
  "columnGap",
  "order",

  // Grid
  "grid",
  "gridArea",
  "gridAutoColumns",
  "gridAutoFlow",
  "gridAutoRows",
  "gridColumn",
  "gridColumnEnd",
  "gridColumnGap",
  "gridColumnStart",
  "gridGap",
  "gridRow",
  "gridRowEnd",
  "gridRowGap",
  "gridRowStart",
  "gridTemplate",
  "gridTemplateAreas",
  "gridTemplateColumns",
  "gridTemplateRows",

  // Table layout
  "tableLayout",
  "captionSide",
  "emptyCells",

  // List styling
  "listStyle",
  "listStyleType",
  "listStylePosition",

  // Transform and animation (safe visual transforms only)
  "transform",
  "transformOrigin",
  "transition",
  "transitionDelay",
  "transitionDuration",
  "transitionProperty",
  "transitionTimingFunction",

  // Other safe visual properties
  "opacity",
  "visibility",
  "zIndex",
  "cursor",
  "pointerEvents",
  "boxShadow",
  "textShadow",
  "verticalAlign",
  "float",
  "clear",
  "resize",
  "objectFit",
  "objectPosition",
  "clipPath",
  "filter",
  "backdropFilter",
  "mixBlendMode",
  "isolation",
  "willChange",
  "contain",
  "contentVisibility",
  "aspectRatio",
]);

/**
 * Validates that a CSS value doesn't contain dangerous patterns.
 * Blocks:
 * - url() functions (can load external resources)
 * - expression() (IE code execution)
 * - javascript: protocol
 * - import statements
 */
const isSafeCssValue = (value: string): boolean => {
  const lowerValue = value.toLowerCase();

  // Block dangerous patterns
  const dangerousPatterns = [
    /url\s*\(/i, // url() can load external resources
    /expression\s*\(/i, // IE expression() executes JavaScript
    /javascript:/i, // javascript: protocol
    /@import/i, // @import can load external stylesheets
    /behavior\s*:/i, // IE behavior property
    /binding\s*:/i, // Mozilla binding property
    /-moz-binding/i, // Firefox binding
  ];

  return !dangerousPatterns.some((pattern) => pattern.test(lowerValue));
};

const parseStyleAttribute = (
  styleValue: string | null,
): React.CSSProperties | undefined => {
  if (!styleValue) return undefined;

  const styles = styleValue
    .split(";")
    .map((item) => item.trim())
    .filter(Boolean)
    .reduce(
      (accumulator, declaration) => {
        const [property, rawValue] = declaration.split(":");
        if (!property || !rawValue) {
          return accumulator;
        }

        const key = toCamelCase(property.trim());
        const value = rawValue.trim();

        // Only allow whitelisted properties
        if (!SAFE_CSS_PROPERTIES.has(key)) {
          console.warn(`Blocked unsafe CSS property: ${property}`);
          return accumulator;
        }

        // Validate the value doesn't contain dangerous patterns
        if (!isSafeCssValue(value)) {
          console.warn(`Blocked unsafe CSS value for ${property}: ${value}`);
          return accumulator;
        }

        (accumulator as Record<string, string>)[key] = value;
        return accumulator;
      },
      {} as Record<string, string>,
    );

  return Object.keys(styles).length > 0
    ? (styles as React.CSSProperties)
    : undefined;
};

const createKeyFactory = () => {
  const counter = new Map<string, number>();
  return (base: string) => {
    const count = counter.get(base) ?? 0;
    counter.set(base, count + 1);
    return `${base}-${count}`;
  };
};

const sanitizeKeyBase = (base: string) => {
  const trimmed = base.trim();
  const normalized = trimmed.length > 0 ? trimmed : "blank";
  return normalized.replace(/\s+/g, "-").slice(0, 24);
};

const describeNode = (node: ChildNode) => {
  if (node.nodeType === NODE_TYPE.TEXT) {
    const content = node.textContent ?? "";
    return `text-${sanitizeKeyBase(content)}`;
  }
  if (node.nodeType === NODE_TYPE.ELEMENT) {
    const element = node as HTMLElement;
    const attrs = element
      .getAttributeNames()
      .sort()
      .map((name) => `${name}:${element.getAttribute(name) ?? ""}`)
      .join("|");
    return `el-${element.tagName.toLowerCase()}-${attrs}`;
  }
  return `node-${node.nodeType}`;
};

// -------------------------------------------------------------
// Sanitization helpers
// -------------------------------------------------------------

export const convertPlainTextToHtml = (value: string) =>
  value
    .split("\n")
    .map((line) => {
      const headingMatch = line.match(HEADING_REGEX);
      if (headingMatch) {
        const level = Math.min(headingMatch[1].length, 6);
        const content = applyInlineMarkdown(headingMatch[2]);
        return `<h${level}>${content}</h${level}>`;
      }
      if (!line) return "<div><br /></div>";
      const content = applyInlineMarkdown(line);
      return `<div>${content}</div>`;
    })
    .join("");

export const sanitizeHtml = (value: string): string => {
  if (typeof window === "undefined") {
    console.error(
      "HTML sanitization is not supported in a non-browser environment. Returning an empty string for safety.",
    );
    return "";
  }

  const domPurify = getDomPurify();
  if (!domPurify) {
    console.error("DOMPurify is not available. Cannot sanitize HTML.");
    return "";
  }

  return domPurify.sanitize(value, {
    USE_PROFILES: { html: true },
  });
};

export const convertBodyToHtml = (value: string): string => {
  const normalized = value ?? "";
  if (!normalized) return "";
  if (isHtmlLike(normalized)) {
    return sanitizeHtml(normalized);
  }
  return convertPlainTextToHtml(normalized);
};

// -------------------------------------------------------------
// React node conversion
// -------------------------------------------------------------

const convertPlainTextToNodes = (value: string): React.ReactElement[] => {
  const lines = value.split("\n");
  const createKey = createKeyFactory();
  const nodes: React.ReactElement[] = [];

  lines.forEach((line, index) => {
    const keyBase = sanitizeKeyBase(line || "line");
    const headingMatch = line.match(HEADING_REGEX);
    if (headingMatch) {
      const level = Math.min(headingMatch[1].length, 6);
      nodes.push(
        React.createElement(
          `h${level}`,
          { key: createKey(`heading-${keyBase}`), className: "font-semibold" },
          headingMatch[2],
        ),
      );
    } else {
      const key = createKey(`line-${keyBase}-${index}`);
      nodes.push(
        React.createElement(
          React.Fragment,
          { key },
          line,
          index < lines.length - 1 ? React.createElement("br") : null,
        ),
      );
    }
  });

  return nodes;
};

const convertHtmlNodeToReact = (
  node: ChildNode,
  createKey: (base: string) => string,
): React.ReactNode | null => {
  const key = createKey(describeNode(node));
  if (node.nodeType === NODE_TYPE.TEXT) {
    return React.createElement(React.Fragment, { key }, node.textContent);
  }

  if (node.nodeType === NODE_TYPE.ELEMENT) {
    const element = node as HTMLElement;
    const tagName = element.tagName.toLowerCase();

    if (DISALLOWED_HTML_TAGS.has(tagName)) {
      return null;
    }

    const children = Array.from(element.childNodes)
      .map((child) => convertHtmlNodeToReact(child, createKey))
      .filter((child): child is React.ReactNode => child !== null);

    const props: Record<string, unknown> = { key };
    for (const attribute of element.getAttributeNames()) {
      if (attribute.toLowerCase().startsWith("on")) {
        continue;
      }

      const value = element.getAttribute(attribute);
      if (attribute === "class") {
        props.className = value ?? undefined;
      } else if (attribute === "style") {
        const styleObject = parseStyleAttribute(value);
        if (styleObject) {
          props.style = styleObject;
        }
      } else {
        props[attribute] = value ?? undefined;
      }
    }

    return React.createElement(
      tagName,
      props,
      children.length > 0 ? children : undefined,
    );
  }

  return null;
};

export const htmlStringToNodes = (value: string): React.ReactNode[] => {
  if (!value) return [];

  if (!isHtmlLike(value)) {
    return convertPlainTextToNodes(value);
  }

  if (!canUseDomParser()) {
    const textOnly = value.replace(/<[^>]*>/g, "");
    return convertPlainTextToNodes(textOnly);
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(value, "text/html");
  const createKey = createKeyFactory();
  const nodes = Array.from(doc.body.childNodes)
    .map((child) => convertHtmlNodeToReact(child, createKey))
    .filter((child): child is React.ReactNode => child !== null);

  if (nodes.length === 0) {
    return [
      React.createElement(
        React.Fragment,
        { key: "empty-html" },
        doc.body.textContent ?? "",
      ),
    ];
  }

  return nodes;
};
